import { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { STATE } from 'js/constants/states';
import { isAU } from 'js/constants/crux';
import UndisclosedSalesWithConnect from 'js/components/common/undisclosedSales/UndisclosedSalesWithConnect';
import SalesHistoryModal from 'js/components/searchResults/SalesHistoryModal';
import { trackEvent } from 'js/actions/segment';
import Segment from 'js/helpers/Segment';
import PreviousSalesLinkSpan from 'js/components/common/PreviousSalesLinkSpan';

const SaleTransactionSummary = ({ isSaleTypeHidden = false, ...props}) => {
    const [showSalesModal, setShowSalesModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const dispatch = useDispatch();
    const featureFlag = useSelector(state => state.launchDarkly.get('featureFlag'));
    const isAllSalesEnabled = featureFlag['feature-all-sales'] === true;
    const _type = props.saleData?.type;
    const soldDate = props.state?.toLowerCase() === STATE.SA ?
        props.saleData?.settlementDate : props.saleData?.contractDate;

    let saleType = '';
    if (props.saleData?.isAgentsAdvice) {
        saleType = 'Pending Settlement Advice';
    } else if (_type && !(isAU && _type === 'Unknown')) {
        saleType = _type;
    }

    const handleSalesLinkClick = useCallback((event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setShowSalesModal(true);
        
        dispatch(trackEvent(Segment.showAllSales({
            page: props.page,
            searchType: props.searchType,
            searchTab: props.searchTab,
            propertyId: props.propertyId,
            resultView: props.resultView,
            searchString: props.searchString,
        })));
    }, [dispatch, props.page, props.searchType, props.searchTab, props.propertyId, props.resultView, props.searchString]);

    const handleModalClose = useCallback(() => {
        setShowSalesModal(false);
        setAnchorEl(null);
    }, []);

    return (
        <Fragment>
            <Typography 
                sx={{
                    ...props.sx,
                    whiteSpace: 'normal !important',
                    display: 'inline',
                }} 
                variant="h4"
            >
                <UndisclosedSalesWithConnect
                    id={props.propertyId}
                    soldDate={soldDate && soldDate !== '' ? soldDate : ''}
                    saleType={_type}
                    salePrice={props.saleData?.price}
                    isPriceWithheld={props.saleData?.isPriceWithheld}
                    isAgentsAdvice={props.saleData?.isAgentsAdvice}
                    containerName="attribute-panel"
                    noPriceAvailableText="Price not available"
                />
                {isAllSalesEnabled && props.salesLinkText && (
                    <PreviousSalesLinkSpan
                        className="previous-sales-link"
                        customStyles={{
                            marginLeft: '8px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={handleSalesLinkClick}
                    >
                        {props.salesLinkText}
                    </PreviousSalesLinkSpan>
                )}
            </Typography>
            {
                !isSaleTypeHidden && <p id="sale-type">{saleType}</p>
            }
            
            {isAllSalesEnabled && props.salesList && props.salesList.length > 0 && (
                <SalesHistoryModal
                    isOpen={showSalesModal}
                    onClose={handleModalClose}
                    anchorEl={anchorEl}
                    salesList={props.salesList}
                    propertyId={props.propertyId}
                    addressState={props.state}
                    undisclosedSalesAccess={props.undisclosedSalesAccess}
                    undisclosedSalesFeature={props.undisclosedSalesFeature}
                    getTransactionType={props.getTransactionType}
                    page={props.page}
                    searchType={props.searchType}
                    searchTab={props.searchTab}
                    resultView={props.resultView}
                />
            )}
        </Fragment>
    );
};

SaleTransactionSummary.propTypes = {
    propertyId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]).isRequired,
    state: PropTypes.string,
    saleData: PropTypes.shape({
        type: PropTypes.string,
        price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        settlementDate: PropTypes.string,
        contractDate: PropTypes.string,
        isPriceWithheld: PropTypes.bool,
        isAgentsAdvice: PropTypes.bool,
    }),
    isSaleTypeHidden: PropTypes.bool,
    sx: PropTypes.object,
    salesList: PropTypes.array,
    salesLinkText: PropTypes.string,
    undisclosedSalesAccess: PropTypes.bool,
    undisclosedSalesFeature: PropTypes.bool,
    getTransactionType: PropTypes.func,
    page: PropTypes.string,
    searchType: PropTypes.string,
    searchTab: PropTypes.string,
    resultView: PropTypes.string,
    searchString: PropTypes.string,
};

export default SaleTransactionSummary;
